import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { ITrackAction, TrackingService, TrackingType, trackingConstants } from '@frontend/sports/tracking/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, take, tap } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { V1PickId, V2OptionMarketPickId } from '../../../core/picks/pick-id';
import { betslipTypesBaseSelector } from '../../types/base/selectors';
import { LineSwitcherActions } from '../actions';

interface LineSwitcherTrackData {
    label: string;
    action: string;
}

@Injectable()
export class LineSwitcherTrackingEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private trackingService: TrackingService,
    ) {}

    handleTrackLineSwitcherButtonClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.initLineSwitcher),
                tap(() => {
                    this.doTracking({
                        type: TrackingType.Referring,
                        data: {
                            label: 'Line_Switcher_Entry_Point',
                            action: 'Line_Switcher_Icon_Click',
                        },
                    } as ITrackAction);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleSwap$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.trackSwap),
                map((action) => {
                    const oddsString =
                        action.newPick.currentPrice!.nativeOdds.decimals >= action.oldPick.currentPrice!.nativeOdds.decimals
                            ? 'HigherOdds'
                            : 'LowerOdds';

                    const sportId = action.newPick.sportId;
                    const marketTemplateId = BetslipV1Pick.isPick(action.newPick) ? action.newPick.market.templateId : '-1';
                    const newOptionId = action.newPick.id as V1PickId | V2OptionMarketPickId;
                    const oldOptionId = action.oldPick.id as V1PickId | V2OptionMarketPickId;

                    this.doTracking({
                        type: TrackingType.Referring,
                        data: {
                            label: `${sportId}_${marketTemplateId}_${oldOptionId.optionId}_${newOptionId.optionId}_Changed_${oddsString}`,
                            action: 'Line_Switcher_Confirm_Changes',
                        },
                    } as ITrackAction);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleTrackLineSwitcherCloseButtonClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.cancelLineSwitcher),
                tap(() => {
                    this.doTracking({
                        type: TrackingType.Referring,
                        data: {
                            label: 'Line_Switcher_Screen',
                            action: 'Screen_Closed',
                        },
                    } as ITrackAction);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleTrackLineSwitcherErrorEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.updateErrorState),
                tap((action) => {
                    this.doTracking({
                        type: TrackingType.Referring,
                        data: {
                            label: action.payload.message,
                            action: 'Line_Switcher_Error',
                        },
                    } as ITrackAction);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleTrackHelpMessageBoxInitEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.initHelpMessageBox),
                tap(() => {
                    this.doTracking({
                        type: TrackingType.Referring,
                        data: {
                            label: 'Onboarding_Message',
                            action: 'Message_Displayed',
                        },
                    } as ITrackAction);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleTrackHelpMessageBoxCloseButtonClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LineSwitcherActions.closeHelpMessageBox),
                tap(() => {
                    this.doTracking({
                        type: TrackingType.Referring,
                        data: {
                            label: 'Onboarding_Message',
                            action: 'Message_Closed',
                        },
                    } as ITrackAction);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private doTracking(tracker: ITrackAction): void {
        this.store
            .select(betslipTypesBaseSelector)
            .pipe(
                map((types) => types.currentSelectedType),
                take(1),
            )
            .subscribe((currrentSelectedType) => {
                const tabName = currrentSelectedType || '';
                const data = tracker.data as LineSwitcherTrackData;

                switch (tracker.type) {
                    case TrackingType.PageName:
                        this.trackingService.track(tracker.name, tracker.data || {});
                        break;
                    case TrackingType.Referring:
                        this.trackingService.triggerEventWithCleaning(trackingConstants.EVENT_LINE_SWITCHER, {
                            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Line_Switcher',
                            [trackingConstants.COMPONENT_LABEL_EVENT]: data.label,
                            [trackingConstants.COMPONENT_ACTION_EVENT]: data.action,
                            [trackingConstants.COMPONENT_LOCATION_EVENT]: `Line_Switcher_Tab_${tabName}`,
                        });
                        break;
                }
            });
    }
}
